import { useCallback } from "react";
import { FieldValues, useController } from "react-hook-form";

import { CountriesSelect, ICountriesSelectProps } from "../input/CountriesSelect";
import { FieldPathOfType, IControllerProps } from "./formTypes";

type IControlledCountriesSelectProps = Omit<ICountriesSelectProps, "value">;

interface IProps<TFieldValues extends FieldValues, TName extends FieldPathOfType<TFieldValues, string[]>>
    extends IControlledCountriesSelectProps {
    name: TName;
    controllerProps?: IControllerProps<TFieldValues, TName, string[]>;
}

export function ControllerCountriesSelect<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPathOfType<TFieldValues, string[]> = FieldPathOfType<TFieldValues, string[]>,
>({
    name,
    controllerProps,
    onChange,
    onBlur,
    ...countrySelectProps
}: IProps<TFieldValues, TName>): React.ReactElement | null {
    const {
        field: { value, onChange: onFieldChange, onBlur: onFieldBlur, ref },
    } = useController({ ...controllerProps, name });

    const handleChange = useCallback(
        (option: string[]): void => {
            onFieldChange(option ?? []);
            onChange?.(option ?? []);
        },
        [onFieldChange, onChange],
    );
    const handleBlur = useCallback((): void => {
        onFieldBlur();
        onBlur?.();
    }, [onFieldBlur, onBlur]);

    return (
        <CountriesSelect
            {...countrySelectProps}
            ref={ref}
            value={value ?? []}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
}
